import { ReactElement, useEffect, useState } from 'react';
import {
  BooleanInput,
  Datagrid,
  FilterForm,
  FunctionField,
  List,
  Pagination,
  SelectInput,
  TextInput,
  TopToolbar,
  useNotify,
  useTranslate,
} from 'react-admin';
import { Box, Button, Card, Chip, Dialog, Stack, Tooltip, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { DashboardContent } from 'src/layouts/dashboard';
import { CustomBreadcrumbs } from 'src/components/custom-breadcrumbs';
import { PermissionGuard } from 'src/routes/components/permission-guard';
import { EmptyContent } from 'src/components/empty-content';
import ErrorPage403 from 'src/pages/error/403';
import {
  HotWalletItem,
  ProviderItem,
  listHotWallets,
  listProviders,
} from '../../../services/xbitRelaySweepService';
import {
  DEFAULT_SWEEP_PROVIDER,
  XBIT_RELAY_SWEEP_PERMISSION_RESOURCE,
} from './constants';
import { CollectDialog } from './CollectDialog';
import { IncidentSweepDialog } from './IncidentSweepDialog';
import { chainSymbol, statusColor, truncate } from './helpers';
import './init';

const SweepMonitorListActions = ({
  providers,
  chains: chainChoices,
  onIncidentSweep,
}: {
  providers: ProviderItem[];
  chains: HotWalletItem[];
  onIncidentSweep: () => void;
}) => {
  const t = useTranslate();
  return (
  <TopToolbar
    sx={{
      minHeight: 90,
      alignItems: 'center',
      px: 3,
      pt: 1,
      overflow: 'visible',
      width: '100%',
    }}
  >
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        flexWrap: 'wrap',
        '& form': { display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', width: '100%' },
        '& .ra-input': { mt: 0, mb: 0 },
        '& .MuiFormControl-root': { mt: 0, mb: 0 },
        '& .MuiInputBase-root': { minHeight: 40 },
      }}
    >
      <FilterForm
        filters={[
          <TextInput
            key="userId"
            source="userId"
            label="module.xbitRelaySweepMonitor.userId"
            alwaysOn
            resettable
            size="small"
            sx={{ minWidth: 200 }}
          />,
          <SelectInput
            key="provider"
            source="provider"
            label="module.xbitRelaySweepMonitor.provider"
            choices={providers.map((p) => ({ id: p.provider, name: p.provider }))}
            alwaysOn
            resettable
            emptyText="ALL"
            size="small"
            sx={{ minWidth: 200 }}
          />,
          <SelectInput
            key="chainId"
            source="chainId"
            label="module.xbitRelaySweepMonitor.chain"
            choices={chainChoices.map((c) => ({
              id: c.chainId,
              name: `${c.chainName} (${c.chainId})`,
            }))}
            alwaysOn
            resettable
            emptyText="ALL"
            size="small"
            sx={{ minWidth: 220 }}
          />,
          <TextInput
            key="address"
            source="address"
            label="module.xbitRelaySweepMonitor.address"
            alwaysOn
            resettable
            size="small"
            sx={{ minWidth: 220 }}
          />,
          <BooleanInput
            key="hideZeroBalance"
            source="hideZeroBalance"
            label="module.xbitRelaySweepMonitor.hideZeroBalance"
            alwaysOn
            size="small"
          />,
        ]}
      />
      {/* POST /xbit-relay/sweep/incident is guarded by xbit-relay:sweep:incident on the BE */}
      <PermissionGuard
        resource={XBIT_RELAY_SWEEP_PERMISSION_RESOURCE}
        action="incident"
        fallback={<></>}
      loadingFallback={<></>}
      >
        <Button
          variant="contained"
          color="error"
          size="small"
          onClick={onIncidentSweep}
          sx={{ ml: 'auto', whiteSpace: 'nowrap' }}
        >
          {t('module.xbitRelaySweepMonitor.incidentSweep')}
        </Button>
      </PermissionGuard>
    </Box>
  </TopToolbar>
  );
};

const XbitRelaySweepMonitorList = () => {
  const t = useTranslate();
  const notify = useNotify();
  const [providers, setProviders] = useState<ProviderItem[]>([]);
  const [chainChoices, setChainChoices] = useState<HotWalletItem[]>([]);
  const [popup, setPopup] = useState<ReactElement>();

  useEffect(() => {
    listProviders()
      .then((res) => setProviders(res.data?.providers ?? []))
      .catch(() => setProviders([]));
  }, []);

  useEffect(() => {
    listHotWallets(DEFAULT_SWEEP_PROVIDER)
      .then((res) => setChainChoices(res.data?.wallets ?? []))
      .catch(() => setChainChoices([]));
  }, []);

  const copy = (value: string) => {
    if (!value) return;
    navigator.clipboard.writeText(value);
    notify('Copied to clipboard', { type: 'info' });
  };

  return (
    <DashboardContent>
      <PermissionGuard
        resource={XBIT_RELAY_SWEEP_PERMISSION_RESOURCE}
        action="read"
        fallback={<ErrorPage403 />}
      >
        <CustomBreadcrumbs
          heading={t('module.xbitRelaySweepMonitor.title')}
          links={[
            { name: t('resource.xbitRelay'), href: '#' },
            { name: t('module.xbitRelaySweepMonitor.title') },
            { name: t('resource.list') },
          ]}
        />

        <Card sx={{ mt: 3, pb: 2 }}>
          <List
            actions={
              <SweepMonitorListActions
                providers={providers}
                chains={chainChoices}
                onIncidentSweep={() =>
                  setPopup(
                    <IncidentSweepDialog
                      providers={providers}
                      onClose={() => setPopup(undefined)}
                    />,
                  )
                }
              />
            }
            empty={false}
            perPage={20}
            exporter={false}
            pagination={<Pagination rowsPerPageOptions={[10, 20, 50, 100]} />}
            sort={{ field: 'lastActive', order: 'DESC' }}
          >
            <Box sx={{ overflow: 'auto' }}>
              <Datagrid
                rowClick={false}
                bulkActionButtons={false}
                empty={<EmptyContent />}
                sx={{
                  '& .RaDatagrid-headerCell': { fontWeight: 600 },
                  '& .RaDatagrid-rowCell': { py: 1.25 },
                }}
              >
                <FunctionField
                  label="module.xbitRelaySweepMonitor.lastActive"
                  source="lastActive"
                  render={(record: any) =>
                    record.lastActiveUnixMs ? (
                      <Tooltip
                        title={dayjs(Number(record.lastActiveUnixMs)).format(
                          'YYYY-MM-DD HH:mm:ss',
                        )}
                      >
                        <Typography
                          variant="body2"
                          sx={{ whiteSpace: 'nowrap', fontFamily: 'monospace' }}
                        >
                          {dayjs(Number(record.lastActiveUnixMs)).format('MM-DD HH:mm')}
                        </Typography>
                      </Tooltip>
                    ) : (
                      '-'
                    )
                  }
                />
                <FunctionField
                  label="module.xbitRelaySweepMonitor.chain"
                  sortable={false}
                  render={(record: any) => (
                    <Tooltip title={String(record.chainId)}>
                      <Chip
                        label={chainSymbol(record.chainId)}
                        size="small"
                        variant="outlined"
                      />
                    </Tooltip>
                  )}
                />
                <FunctionField
                  label="module.xbitRelaySweepMonitor.userId"
                  sortable={false}
                  render={(record: any) =>
                    record.userId ? (
                      <Tooltip title={record.userId}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: 'monospace',
                            cursor: 'pointer',
                            whiteSpace: 'nowrap',
                          }}
                          onClick={() => copy(record.userId)}
                        >
                          {truncate(record.userId)}
                        </Typography>
                      </Tooltip>
                    ) : (
                      '-'
                    )
                  }
                />
                <FunctionField
                  label="module.xbitRelaySweepMonitor.provider"
                  sortable={false}
                  render={(record: any) => (
                    <Typography variant="body2">{record.provider || '-'}</Typography>
                  )}
                />
                <FunctionField
                  label="module.xbitRelaySweepMonitor.token"
                  sortable={false}
                  render={(record: any) => (
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {record.symbol || '-'}
                    </Typography>
                  )}
                />
                <FunctionField
                  label="module.xbitRelaySweepMonitor.balance"
                  source="balance"
                  render={(record: any) => (
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      {record.balance || '-'}
                    </Typography>
                  )}
                />
                <FunctionField
                  label="module.xbitRelaySweepMonitor.status"
                  sortable={false}
                  render={(record: any) =>
                    record.latestStatus ? (
                      <Chip
                        label={record.latestStatus}
                        size="small"
                        color={statusColor(record.latestStatus) as any}
                        variant={record.latestStatus === 'SKIPPED' ? 'outlined' : 'filled'}
                      />
                    ) : (
                      '-'
                    )
                  }
                />
                <FunctionField
                  label="module.xbitRelaySweepMonitor.address"
                  sortable={false}
                  render={(record: any) => (
                    <Tooltip title={record.address || ''}>
                      <Typography
                        variant="body2"
                        sx={{ fontFamily: 'monospace', cursor: 'pointer' }}
                        onClick={() => copy(record.address)}
                      >
                        {truncate(record.address)}
                      </Typography>
                    </Tooltip>
                  )}
                />
                <FunctionField
                  label=""
                  sortable={false}
                  render={(record: any) => (
                    <PermissionGuard
                      resource={XBIT_RELAY_SWEEP_PERMISSION_RESOURCE}
                      action="collect"
                      fallback={<></>}
                    loadingFallback={<></>}
                    >
                      <Stack direction="row" justifyContent="flex-end">
                        <Button
                          variant="outlined"
                          size="small"
                          color="primary"
                          onClick={(e) => {
                            e.preventDefault();
                            setPopup(
                              <CollectDialog
                                record={record}
                                onClose={() => setPopup(undefined)}
                              />,
                            );
                          }}
                        >
                          {t('module.xbitRelaySweepMonitor.collect')}
                        </Button>
                      </Stack>
                    </PermissionGuard>
                  )}
                />
              </Datagrid>
            </Box>
          </List>
        </Card>

        <Dialog
          open={Boolean(popup)}
          onClose={() => setPopup(undefined)}
          maxWidth="sm"
          fullWidth
        >
          {popup}
        </Dialog>
      </PermissionGuard>
    </DashboardContent>
  );
};

export default XbitRelaySweepMonitorList;
